import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Save, Trash2 } from "lucide-react";

const EMPTY = { name: "", start_time: "08:00", end_time: "13:00", color: "#5B7FE2", order: 10, active: true };
const err = (e) => toast.error(e.response?.data?.detail || "Errore");

export function SlotsTab({ slots }) {
  const qc = useQueryClient();
  const refresh = () => { qc.invalidateQueries({ queryKey: ["vig-slots"] }); qc.invalidateQueries({ queryKey: ["vig-shifts"] }); };
  const saveMut = useMutation({
    mutationFn: async (s) => (s.id ? api.patch(`/vigilanza/slots/${s.id}`, s) : api.post("/vigilanza/slots", s)),
    onSuccess: () => { toast.success("Fascia salvata"); refresh(); }, onError: err,
  });
  const delMut = useMutation({
    mutationFn: async (id) => api.delete(`/vigilanza/slots/${id}`),
    onSuccess: () => { toast.success("Fascia eliminata"); refresh(); }, onError: err,
  });
  return (
    <div className="space-y-3 mt-4">
      <p className="text-sm text-stone-500">Se l'orario di fine è prima dell'inizio (es. 22:00 → 07:00) il turno finisce la mattina del giorno dopo.</p>
      {slots.map((s) => <SlotForm key={s.id} initial={s} onSave={saveMut.mutate} onDelete={() => window.confirm(`Eliminare la fascia ${s.name} con tutti i suoi turni?`) && delMut.mutate(s.id)}/>)}
      <SlotForm key={`new-${slots.length}`} initial={{ ...EMPTY, order: slots.length + 1 }} onSave={saveMut.mutate} isNew/>
    </div>
  );
}

function SlotForm({ initial, onSave, onDelete, isNew }) {
  const [f, setF] = useState(initial);
  const tid = isNew ? "new" : initial.id;
  return (
    <Card className={`p-3 border shadow-none rounded-xl flex flex-wrap items-center gap-3 ${isNew ? "border-dashed border-stone-300" : "border-stone-200"}`} data-testid={`vig-slot-form-${tid}`}>
      <input type="color" value={f.color} onChange={(e) => setF({ ...f, color: e.target.value })} className="h-8 w-8 rounded cursor-pointer border-0" data-testid={`vig-slot-color-${tid}`}/>
      <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder={isNew ? "Nuova fascia (es. Mattino)" : "Nome"} className="h-9 w-48" data-testid={`vig-slot-name-${tid}`}/>
      <Input type="time" value={f.start_time} onChange={(e) => setF({ ...f, start_time: e.target.value })} className="h-9 w-28" data-testid={`vig-slot-start-${tid}`}/>
      <span className="text-stone-400">→</span>
      <Input type="time" value={f.end_time} onChange={(e) => setF({ ...f, end_time: e.target.value })} className="h-9 w-28" data-testid={`vig-slot-end-${tid}`}/>
      <Input type="number" value={f.order} onChange={(e) => setF({ ...f, order: Number(e.target.value) })} className="h-9 w-20" title="Ordine" data-testid={`vig-slot-order-${tid}`}/>
      <label className="flex items-center gap-2 text-xs text-stone-600"><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid={`vig-slot-active-${tid}`}/> Attiva</label>
      <div className="ml-auto flex gap-1">
        <Button size="sm" onClick={() => onSave(f)} disabled={!f.name.trim()} className="bg-[#1C352D] hover:bg-[#2a4a40]" data-testid={`vig-slot-save-${tid}`}>
          {isNew ? <><Plus size={14} className="mr-1"/> Aggiungi</> : <><Save size={14} className="mr-1"/> Salva</>}
        </Button>
        {!isNew && <Button size="icon" variant="ghost" onClick={onDelete} data-testid={`vig-slot-del-${tid}`}><Trash2 size={14} className="text-red-600"/></Button>}
      </div>
    </Card>
  );
}
